import { createClient } from 'contentful';

// Initialize Contentful client
const client = createClient({
  space: process.env.REACT_APP_CONTENTFUL_SPACE_ID || '',
  accessToken: process.env.REACT_APP_CONTENTFUL_ACCESS_TOKEN || '',
});

// Fetch announcements from Contentful (ordered by date, newest first)
export const fetchAnnouncements = async () => {
  try {
    const response = await client.getEntries({
      content_type: 'announcement',
      order: '-fields.date,-sys.createdAt',
    });

    return response.items.map(item => ({
      id: item.sys.id,
      date: item.fields.date || '',
      announcement: item.fields.announcement || '',
    }));
  } catch (error) {
    console.error('Error fetching announcements:', error);
    return [];
  }
};

// Fetch publications from Contentful
export const fetchPublications = async () => {
  try {
    const response = await client.getEntries({
      content_type: 'publication',
      order: '-fields.year,-sys.createdAt',
    });
    
    return response.items.map(item => ({
      id: item.sys.id,
      title: item.fields.title,
      authors: item.fields.authors,
      journal: item.fields.journal,
      year: item.fields.year ? item.fields.year.toString() : '',
      doi: item.fields.doi || '',
      url: item.fields.url || '',
      github: item.fields.github || '',
    }));
  } catch (error) {
    console.error('Error fetching publications:', error);
    return [];
  }
};

// Fetch team members from Contentful
export const fetchTeamMembers = async () => {
  try {
    const response = await client.getEntries({
      content_type: 'teamMember',
      order: 'fields.order,sys.createdAt',
    });
    
    return response.items.map(item => ({
      id: item.sys.id,
      name: item.fields.name,
      title: item.fields.title,
      researchInterest: item.fields.researchInterest,
      education: item.fields.education,
      email: item.fields.email || '',
      image: item.fields.image?.fields?.file?.url ? `https:${item.fields.image.fields.file.url}` : '',
    }));
  } catch (error) {
    console.error('Error fetching team members:', error);
    return [];
  }
};

// Fetch alumni from Contentful
export const fetchAlumni = async () => {
  try {
    const response = await client.getEntries({
      content_type: 'alumni',
      order: 'fields.category,fields.name',
    });
    
    const groupedAlumni = {
      postdoc: [],
      phd: [],
      masters: [],
      undergrad: []
    };
    
    response.items.forEach(item => {
      const alumnus = {
        id: item.sys.id,
        name: item.fields.name,
        researchTopic: item.fields.researchTopic || item.fields.rt,
        currentPosition: item.fields.currentPosition,
        category: item.fields.category?.toLowerCase() || 'undergrad'
      };
      
      const category = alumnus.category;
      if (groupedAlumni[category]) {
        groupedAlumni[category].push(alumnus);
      } else {
        groupedAlumni.undergrad.push(alumnus);
      }
    });
    
    return groupedAlumni;
  } catch (error) {
    console.error('Error fetching alumni:', error);
    return {
      postdoc: [],
      phd: [],
      masters: [],
      undergrad: []
    };
  }
};

// Fetch publications that have a non-empty github URL (for lab software display)
export const fetchPublicationsWithGithub = async () => {
  const publications = await fetchPublications();
  return publications.filter(p => p.github && p.github.trim() !== '');
};

// Fetch software repositories from Contentful
export const fetchSoftwareRepositories = async () => {
  try {
    const response = await client.getEntries({
      content_type: 'software',
      order: 'sys.createdAt',
    });
    
    return response.items.map(item => ({
      id: item.sys.id,
      name: item.fields.name,
      githubUrl: item.fields.githubUrl,
      username: item.fields.username,
      repository: item.fields.repository,
      description: item.fields.description || '',
    }));
  } catch (error) {
    console.error('Error fetching software repositories:', error);
    return [];
  }
};

// Fetch photos from Contentful
export const fetchPhotos = async () => {
  try {
    const response = await client.getEntries({
      content_type: 'photo',
      order: 'fields.order,sys.createdAt',
    });

    return response.items.map(item => ({
      id: item.sys.id,
      src: item.fields.image?.fields?.file?.url ? `https:${item.fields.image.fields.file.url}` : '',
      alt: item.fields.alt || item.fields.title || 'Gallery photo',
      title: item.fields.title || '',
      width: item.fields.image?.fields?.file?.details?.image?.width || 800,
      height: item.fields.image?.fields?.file?.details?.image?.height || 600,
    }));
  } catch (error) {
    console.error('Error fetching photos:', error);
    return [];
  }
};

// Fetch lab life gallery photos from Contentful (content type: labLifePhoto, fields: image, title)
export const fetchLabLifePhotos = async () => {
  try {
    const response = await client.getEntries({
      content_type: 'labLifePhoto',
      order: 'sys.createdAt',
    });

    return response.items.map(item => ({
      id: item.sys.id,
      src: item.fields.image?.fields?.file?.url ? `https:${item.fields.image.fields.file.url}` : '',
      alt: item.fields.title || 'Lab life photo',
      title: item.fields.title || '',
      width: item.fields.image?.fields?.file?.details?.image?.width || 800,
      height: item.fields.image?.fields?.file?.details?.image?.height || 600,
    }));
  } catch (error) {
    console.error('Error fetching lab life photos:', error);
    return [];
  }
};

export const fetchCV = async () => {
  try {
    const response = await client.getEntries({
      content_type: 'cv',
      limit: 1,
    });
    
    if (response.items.length > 0) {
      const cvItem = response.items[0];
      return {
        id: cvItem.sys.id,
        url: cvItem.fields.cv?.fields?.file?.url ? `https:${cvItem.fields.cv.fields.file.url}` : '',
        title: cvItem.fields.title || 'CV',
        fileName: cvItem.fields.cv?.fields?.file?.fileName || 'CV.pdf',
      };
    }
    return null;
  } catch (error) {
    console.error('Error fetching CV:', error);
    return null;
  }
};

// Helper function to check if Contentful is properly configured
export const isContentfulConfigured = () => {
  return !!(process.env.REACT_APP_CONTENTFUL_SPACE_ID && process.env.REACT_APP_CONTENTFUL_ACCESS_TOKEN);
}; 
